import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2 } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@Directive({
    selector: '[requiredMarker]',
    standalone: true,
})
export class RequiredMarkerDirective implements OnInit
{
    @Input() requiredMarker: boolean;

    constructor(
            private _el: ElementRef,
            private _renderer: Renderer2
    ) {
    }

    ngOnInit() {
        const showMarker = this.requiredMarker ? this.requiredMarker : true;
        if (showMarker) {
            const span = this._renderer.createElement('span');
            span.classList.add('required-marker');
            span.textContent = '*';
            this._renderer.appendChild(this._el.nativeElement, span);
        }
    }
}

@UntilDestroy()
@Directive({
    selector: 'mat-option[selectAll]',
    standalone: true,
})
export class SelectAllDirective<T> implements AfterViewInit
{
    @Input() allValues: T[] = [];
    @Output() selectedCountChange = new EventEmitter<number>();

    constructor(
            private _matSelect: MatSelect,
            private _matOption: MatOption<T>,
    ) {
    }

    ngAfterViewInit() {
        const parentFormControl = this._matSelect.ngControl.control;
        if (!parentFormControl) {
            console.warn('Unable to find parent control to attach select all option');
            return;
        }

        // utilizing null for select-all in multiple select list (vs. forcing to any/incorrect type)
        this._matOption.value = null;

        // handle when the select checkbox is toggled
        this._matOption.onSelectionChange.pipe(untilDestroyed(this)).subscribe({
            next: (value) => {
                if (value.isUserInput) {
                    if (value.source.selected) {
                        parentFormControl.setValue(this.allValues);
                        this._matOption.select();
                        this.selectedCountChange.emit(this.allValues.length);
                    } else {
                        parentFormControl.setValue([]);
                        this._matOption.deselect();
                        this.selectedCountChange.emit(0);
                    }
                }
            },
        });

        // handle when an individual option is toggled
        this._matSelect.optionSelectionChanges.pipe(untilDestroyed(this)).subscribe({
            next: (value) => {
                if (value.isUserInput && value.source.value !== this._matOption.value) {
                    if (!value.source.selected) {
                        this._matOption.deselect();
                    } else {
                        if (parentFormControl.value.length === this.allValues.length) {
                            this._matOption.select();
                        }
                    }
                }
            },
        });

        // on open and close of select panel, toggle "select all" to avoid unnecessary null form control value in selected array
        this._matSelect.openedChange.pipe(untilDestroyed(this)).subscribe({
            next: (opened) => {
                if (opened && parentFormControl.value.length === this.allValues.length) {
                    this._matOption.select();
                } else if (!opened && this._matOption.selected) {
                    this._matOption.deselect();
                }
            },
        });
    }
}
